import React, { useState, useEffect, useCallback } from 'react';
import { empresaService, userService, Usuario, UsuarioRequest, Empresa } from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';

interface UserManagementProps {
  company: Empresa;
  onBack: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ company, onBack }) => {
  const [users, setUsers] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [formData, setFormData] = useState<UsuarioRequest>({
    nome: '',
    email: '',
    senha: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    itemName: '',
    onConfirm: () => {},
    isLoading: false
  });

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await empresaService.getUsers(company.id!);
      setUsers(data);
    } catch (error) {
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  }, [company.id]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateField = (name: string, value: string) => {
    const errors = { ...fieldErrors };
    
    switch (name) {
      case 'nome':
        if (!value.trim()) {
          errors.nome = 'Nome é obrigatório';
        } else if (value.length < 3) {
          errors.nome = 'Nome deve ter pelo menos 3 caracteres';
        } else if (value.length > 60) {
          errors.nome = 'Nome deve ter no máximo 60 caracteres';
        } else {
          delete errors.nome;
        }
        break;
      case 'email':
        if (!value.trim()) {
          errors.email = 'Email é obrigatório';
        } else if (!validateEmail(value)) {
          errors.email = 'Email inválido';
        } else {
          delete errors.email;
        }
        break;
      case 'senha':
        if (!value) {
          errors.senha = 'Senha é obrigatória';
        } else if (value.length < 4) {
          errors.senha = 'Senha deve ter pelo menos 4 caracteres';
        } else if (value.length > 20) {
          errors.senha = 'Senha deve ter no máximo 20 caracteres';
        } else {
          delete errors.senha;
        }
        break;
    }
    
    setFieldErrors(errors);
  };

  const handleInputChange = (name: string, value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Validação em tempo real
    setTimeout(() => validateField(name, value), 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validar todos os campos
    validateField('nome', formData.nome);
    validateField('email', formData.email);
    validateField('senha', formData.senha);
    
    // Verificar se há erros
    if (Object.keys(fieldErrors).length > 0) {
      setError('Por favor, corrija os erros nos campos destacados');
      return;
    }
    
    // Validações adicionais
    if (!formData.nome.trim() || formData.nome.length < 3 || formData.nome.length > 60) {
      setError('Nome deve ter entre 3 e 60 caracteres');
      return;
    }
    
    if (!validateEmail(formData.email)) {
      setError('Email inválido');
      return;
    }
    
    if (formData.senha.length < 4 || formData.senha.length > 20) {
      setError('Senha deve ter entre 4 e 20 caracteres');
      return;
    }
    
    try {
      setLoading(true);
      
      await empresaService.createUser(company.id!, formData);
      setSuccessMessage('Usuário criado com sucesso!');
      
      setShowForm(false);
      setFormData({ nome: '', email: '', senha: '' });
      loadUsers();
      
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao criar usuário');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = (userId: string, userName: string) => {
    setDeleteModal({
      isOpen: true,
      title: 'Excluir Usuário',
      message: 'Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.',
      itemName: userName,
      onConfirm: () => confirmDeleteUser(userId),
      isLoading: false
    });
  };

  const confirmDeleteUser = async (userId: string) => {
    setDeleteModal(prev => ({ ...prev, isLoading: true }));
    try {
      await userService.delete(userId);
      setSuccessMessage('Usuário excluído com sucesso!');
      loadUsers();
      setDeleteModal(prev => ({ ...prev, isOpen: false, isLoading: false }));
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao excluir usuário');
      setDeleteModal(prev => ({ ...prev, isLoading: false }));
    }
  };

  return (
    <div className="main-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <button
                    onClick={onBack}
                    className="btn btn-link text-primary p-0 me-3"
                  >
                    <i className="fas fa-arrow-left me-2"></i>
                    Voltar
                  </button>
                  <div>
                    <h4 className="mb-0">Usuários - {company.razao}</h4>
                    <small className="text-muted">CNPJ: {company.cnpj}</small>
                  </div>
                </div>
                <div className="d-flex align-items-center">
                  <span className="badge bg-info me-3">
                    Total: {users.length} usuários
                  </span>
                  <button
                    onClick={() => {
                      setShowForm(true);
                      setFormData({ nome: '', email: '', senha: '' });
                      setError('');
                    }}
                    className="btn btn-primary"
                  >
                    <i className="fas fa-plus me-2"></i>
                    Novo Usuário
                  </button>
                </div>
              </div>

              {successMessage && (
                <div className="alert alert-success alert-dismissible fade show animate__animated animate__fadeInDown animate__pulse" role="alert" style={{transition: 'all 0.3s ease'}}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle bg-success text-white me-3 d-flex align-items-center justify-content-center animate__animated animate__bounceIn" style={{width: '32px', height: '32px'}}>
                      <i className="fas fa-check fa-sm"></i>
                    </div>
                    <div className="flex-grow-1">
                      <strong>Sucesso!</strong>
                      <div>{successMessage}</div>
                      <div className="mt-1 small text-success-emphasis">
                        <i className="fas fa-info-circle me-1"></i>
                        Os dados foram atualizados automaticamente.
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSuccessMessage('')}
                  ></button>
                </div>
              )}

              {error && (
                <div className="alert alert-danger alert-dismissible fade show animate__animated animate__fadeInDown animate__shakeX" role="alert" style={{transition: 'all 0.3s ease'}}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle bg-danger text-white me-3 d-flex align-items-center justify-content-center animate__animated animate__pulse" style={{width: '32px', height: '32px'}}>
                      <i className="fas fa-exclamation-triangle fa-sm"></i>
                    </div>
                    <div className="flex-grow-1">
                      <strong>Erro!</strong>
                      <div>{error}</div>
                      <div className="mt-1 small text-danger-emphasis">
                        <i className="fas fa-info-circle me-1"></i>
                        Verifique os dados e tente novamente.
                      </div>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError('')}
                  ></button>
                </div>
              )}

              {showForm && (
                <div className="card mt-3">
                  <div className="card-header">
                    <h5 className="mb-0">
                      <i className="fas fa-user-plus me-2"></i>
                      Novo Usuário
                    </h5>
                  </div>
                  <div className="card-body">
                    <form onSubmit={handleSubmit}>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Nome *
                          </label>
                          <input
                            type="text"
                            value={formData.nome}
                            onChange={(e) => handleInputChange('nome', e.target.value)}
                            minLength={3}
                            maxLength={60}
                            className={`form-control ${fieldErrors.nome ? 'is-invalid' : ''}`}
                            required
                          />
                          {fieldErrors.nome && (
                            <div className="invalid-feedback">
                              {fieldErrors.nome}
                            </div>
                          )}
                          <div className="form-text">
                            {formData.nome.length}/60 caracteres (mínimo 3)
                          </div>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Email *
                          </label>
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            className={`form-control ${fieldErrors.email ? 'is-invalid' : ''}`}
                            required
                          />
                          {fieldErrors.email && (
                            <div className="invalid-feedback">
                              {fieldErrors.email}
                            </div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">
                            Senha *
                          </label>
                          <div className="input-group">
                            <input
                              type={showPassword ? 'text' : 'password'}
                              value={formData.senha}
                              onChange={(e) => handleInputChange('senha', e.target.value)}
                              minLength={4}
                              maxLength={20}
                              className={`form-control ${fieldErrors.senha ? 'is-invalid' : ''}`}
                              required
                            />
                            {fieldErrors.senha && (
                              <div className="invalid-feedback">
                                {fieldErrors.senha}
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className="btn btn-outline-secondary"
                            >
                              <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                            </button>
                          </div>
                          <div className="form-text">
                            {formData.senha.length}/20 caracteres (mínimo 4)
                          </div>
                        </div>
                      </div>

                      <div className="d-flex gap-2">
                        <button
                          type="submit"
                          disabled={loading}
                          className={`btn btn-primary ${loading ? 'animate__animated animate__pulse animate__infinite' : ''}`}
                          style={{transition: 'all 0.3s ease'}}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Criando...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-user-plus me-2"></i>
                              Criar Usuário
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowForm(false);
                            setError('');
                            setFieldErrors({});
                          }}
                          className="btn btn-secondary"
                          disabled={loading}
                          style={{transition: 'all 0.3s ease'}}
                        >
                          <i className="fas fa-times me-2"></i>
                          Cancelar
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {loading && !showForm && (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="mt-2 text-muted">Carregando usuários...</p>
                </div>
              )}

              {!loading && users.length === 0 && (
                <div className="text-center py-5">
                  <i className="fas fa-users fa-3x text-muted mb-3"></i>
                  <p className="text-muted">Nenhum usuário cadastrado para esta empresa</p>
                </div>
              )}

              {!loading && users.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Nome</th>
                        <th>Email</th>
                        <th>Tipo</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => (
                        <tr key={user.id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="avatar-circle bg-primary text-white me-3">
                                {user.nome.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div className="fw-medium">{user.nome}</div>
                              </div>
                            </div>
                          </td>
                          <td className="text-muted">{user.email}</td>
                          <td>
                            <span className={`badge ${
                              user.administrador ? 'bg-warning' : 'bg-secondary'
                            }`}>
                              {user.administrador ? 'Administrador' : 'Usuário'}
                            </span>
                          </td>
                          <td>
                            {!user.administrador ? (
                              <button
                                onClick={() => handleDeleteUser(user.id!, user.nome)}
                                className="btn btn-sm btn-outline-danger"
                                disabled={loading}
                              >
                                <i className="fas fa-trash me-1"></i>
                                Deletar
                              </button>
                            ) : (
                              <small className="text-muted">
                                <i className="fas fa-shield-alt me-1"></i>
                                Protegido
                              </small>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        title={deleteModal.title}
        message={deleteModal.message}
        itemName={deleteModal.itemName}
        onConfirm={deleteModal.onConfirm}
        onClose={() => setDeleteModal(prev => ({ ...prev, isOpen: false }))}
        isLoading={deleteModal.isLoading}
      />
    </div>
  );
};

export default UserManagement;